import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import Commons from '../Services/Common/Common';
import Global from '../Services/Common/Global';
import { apiRequest } from '../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../util';
import {
  setSearchInfo,
  setCustCd,
  setDepList,
  setDepCd,
} from '../Reducer/SearchReducer';

export default function useCompanyCustDepRedux({
  requireCompany = false,
  requireCust = false,
  requireDep = false,
  autoFetchDep = false, // 사업장 변경 시 부서 자동 조회 (requireDep가 false여도)
  /** autoFetchDep일 때 부서를 첫 항목이 아닌 전체('')로 — 검색조건에 전체 옵션이 있을 때 */
  autoFetchDepDefaultAll = false,
  /** 사업장 콤보에 '전체'가 있을 때 초기값을 ''(전체)로 */
  defaultCustAll = false,
  /** 회사 콤보에 '전체'가 있을 때 초기값을 ''(전체)로 */
  defaultCoAll = false,
}) {
  const dispatch = useDispatch();
  const { companyInfo, custInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const filterCustList = (coCd) => {
    if (!custInfo?.length) return [];
    return custInfo
      .map((item) => ({
        coCd: item.coCd,
        custCd: item.custCd,
        custNm: item.custNm,
      }))
      .filter((item) => String(item.coCd) === String(coCd))
      .map((item) => ({ value: item.custCd, name: item.custNm }));
  };

  const getDepLastName = async (_custCd) => {
    try {
      const encryptedParams = mapParamsBySchema({
        CustCd: (_, context) => Commons.EncryptAES(_custCd || '') || '',
        SearchText: (_, context) => Commons.EncryptAES('') || '',
      });

      const result = await apiRequest(
        'post',
        Global.urls.Utility.getDepLast,
        encryptedParams
      );

      const data = result?.data;

      if (!data || data.length === 0 || data[0]?.type === 'Error') {
        return null;
      }
      return data;
    } catch (e) {
      console.error(e.message);
      return null;
    }
  };

  useEffect(() => {
    if (!companyInfo?.length && requireCompany) {
      dispatch(
        showSnackbar({
          message: '회사 정보를 불러올 수 없습니다.',
          type: 'error',
        })
      );
      return;
    }
    if (!custInfo?.length && requireCust) {
      dispatch(
        showSnackbar({
          message: '사업장 정보를 불러올 수 없습니다.',
          type: 'error',
        })
      );
      return;
    }

    const stored = Commons.Storages('get', 'companyInfo') || {};

    const hasStoredCoCd =
      stored && typeof stored === 'object' && 'coCd' in stored;
    const defaultCoCd = hasStoredCoCd
      ? (stored.coCd ?? '')
      : defaultCoAll
        ? ''
        : (companyInfo?.[0]?.coCd ?? '');
    const coList = (companyInfo || []).map((c) => ({
      value: c.coCd,
      name: c.coNm,
      ceoName: c.ceoNm,
    }));

    const nextCustList = filterCustList(defaultCoCd);
    const hasStoredCustCd =
      stored && typeof stored === 'object' && 'custCd' in stored;
    const defaultCustCd = hasStoredCustCd
      ? (stored.custCd ?? '')
      : defaultCustAll
        ? ''
        : (nextCustList?.[0]?.value ?? '');

    if (requireCust && nextCustList.length === 0) {
      dispatch(
        showSnackbar({
          message: '선택한 회사에 사업장이 없습니다.',
          type: 'error',
        })
      );
    }

    dispatch(
      setSearchInfo({
        coList,
        coCd: defaultCoCd,
        custList: nextCustList,
        custCd: defaultCustCd,
      })
    );

    // requireDep가 true이거나 autoFetchDep가 true이면 초기 부서 조회
    if ((requireDep || autoFetchDep) && defaultCustCd) {
      (async () => {
        const depData = await getDepLastName(defaultCustCd);
        let depList = [];

        if (depData && depData.length > 0) {
          depList = depData.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        } else {
          if (requireDep) {
            dispatch(
              showSnackbar({
                message: '부서 정보를 불러올 수 없습니다.',
                type: 'error',
              })
            );
          }
        }
        dispatch(setDepList(depList));

        if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
          dispatch(setDepCd(''));
        } else if (requireDep || (autoFetchDep && depList.length > 0)) {
          // 사업장과 동일하게 localStorage에 저장된 값을 우선 복원
          const defaultDepCd = stored.depCd || (depList?.[0]?.value ?? '');
          dispatch(setDepCd(defaultDepCd));
        }
      })();
    }
  }, [
    companyInfo,
    custInfo,
    requireCompany,
    requireCust,
    requireDep,
    autoFetchDep,
    autoFetchDepDefaultAll,
    defaultCustAll,
    defaultCoAll,
    dispatch,
  ]);

  const validateSelection = () => {
    if (requireCompany && !searchInfo.coCd) {
      dispatch(showSnackbar({ message: '회사를 선택하세요.', type: 'error' }));
      return false;
    }
    if (requireCust && !searchInfo.custCd) {
      dispatch(
        showSnackbar({ message: '사업장을 선택하세요.', type: 'error' })
      );
      return false;
    }
    if (requireDep && !searchInfo.depCd) {
      dispatch(showSnackbar({ message: '부서를 선택하세요.', type: 'error' }));
      return false;
    }
    return true;
  };

  const handleCoChange = async (raw) => {
    const newCoCd = raw?.target?.value !== undefined ? raw.target.value : raw;
    const nextCustList = filterCustList(newCoCd);
    const nextCustCd =
      requireCust && nextCustList.length > 0 ? nextCustList[0].value : '';

    if (requireCust && nextCustList.length === 0) {
      dispatch(
        showSnackbar({
          message: '선택한 회사에 사업장이 없습니다.',
          type: 'error',
        })
      );
    }

    dispatch(
      setSearchInfo({
        coCd: newCoCd,
        custList: nextCustList,
        custCd: nextCustCd,
      })
    );

    // requireDep가 true이거나 autoFetchDep가 true이면 부서 조회
    if (requireDep || autoFetchDep) {
      dispatch(setDepList([]));
      dispatch(setDepCd(''));

      if (nextCustCd) {
        const depData = await getDepLastName(nextCustCd);
        let depList = [];

        if (depData && depData.length > 0) {
          depList = depData.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        } else {
          if (requireDep) {
            dispatch(
              showSnackbar({
                message: '부서 정보를 불러올 수 없습니다.',
                type: 'error',
              })
            );
          }
        }

        dispatch(setDepList(depList));
        if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
          dispatch(setDepCd(''));
        } else if (requireDep || (autoFetchDep && depList.length > 0)) {
          dispatch(setDepCd(depList?.[0]?.value ?? ''));
        }
      }
    }

    Commons.Storages('set', 'companyInfo', {
      coCd: newCoCd || '',
      custCd: nextCustCd || '',
      depCd: '',
    });

    return nextCustCd;
  };

  const handleCustChange = async (raw) => {
    const newCustCd = raw?.target?.value !== undefined ? raw.target.value : raw;
    dispatch(setCustCd(newCustCd));
    Commons.Storages('set', 'companyInfo', {
      coCd: searchInfo.coCd || '',
      custCd: newCustCd,
      depCd: '',
    });

    // requireDep가 true이거나 autoFetchDep가 true이면 부서 조회
    if (requireDep || autoFetchDep) {
      const depData = await getDepLastName(newCustCd);
      let depList = [];

      if (depData && depData.length > 0) {
        depList = depData.map((item) => ({
          name: item.depNmLast,
          value: item.depCd,
        }));
      } else {
        if (requireDep) {
          dispatch(
            showSnackbar({
              message: '부서 정보를 불러올 수 없습니다.',
              type: 'error',
            })
          );
        }
      }

      dispatch(setDepList(depList));
      if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
        dispatch(setDepCd(''));
      } else if (requireDep || (autoFetchDep && depList.length > 0)) {
        dispatch(setDepCd(depList?.[0]?.value ?? ''));
      }
    }

    return newCustCd;
  };

  const handleDepChange = (newDepCd) => {
    dispatch(setDepCd(newDepCd));
    // 사업장과 동일하게 localStorage에 저장
    Commons.Storages('set', 'companyInfo', {
      coCd: searchInfo.coCd || '',
      custCd: searchInfo.custCd || '',
      depCd: newDepCd || '',
    });
  };

  return {
    handleCoChange,
    handleCustChange,
    handleDepChange,
    validateSelection,
  };
}
